import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  Output,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { ChipsComponent, ToastService } from '@teorema/patron';
import { Observable, Subject, firstValueFrom, of } from 'rxjs';
import { catchError, debounceTime, switchMap, takeUntil, tap } from 'rxjs/operators';
import { FavoriteSelectComponent } from '../../../shared/favorite-input';
import { FavoriteListKey } from '../../../../core/models/favorite-list.model';
import { TicketQualityService } from '../../../../core/services/ticket-quality.service';
import { TicketService } from '../../../../core/services/ticket.service';
import { QualityPublication } from '../../../../core/models/ticket.model';

@Component({
  selector: 'app-quality-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    CheckboxModule,
    DatePickerModule,
    DialogModule,
    ChipsComponent,
    FavoriteSelectComponent,
  ],
  templateUrl: './quality-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QualityFormComponent implements OnDestroy {
  @Output() onSave = new EventEmitter<void>();

  protected readonly FavoriteKey = FavoriteListKey;

  visible = signal(false);
  saving = signal(false);
  statusesLoading = signal(false);
  statuses = signal<any[]>([]);
  usersLoading = signal(false);
  users = signal<any[]>([]);

  publicar = signal(false);

  editingId: string | null = null;
  ticketId: string = '';
  tags: string[] = [];

  form: FormGroup;

  private readonly statusSearch$ = new Subject<string>();
  private readonly userSearch$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  /**
   * Registro selecionado na abertura, para não desaparecer da listagem. A busca devolve
   * uma página de 20 e o valor salvo raramente está nela — sem o seed o campo abriria
   * vazio ao editar, e perderia o rótulo assim que o usuário digitasse qualquer coisa.
   */
  private statusSeed: { id: string; description: string } | null = null;
  private userSeed: { id: string; name: string } | null = null;

  private readonly fb = inject(FormBuilder);
  private readonly qualityService = inject(TicketQualityService);
  private readonly ticketService = inject(TicketService);
  private readonly toastService = inject(ToastService);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor() {
    this.form = this.fb.group({
      titulo: [''],
      descricao: [''],
      link: [''],
      data: [null],
      ticket_status_id: [null],
      user_id: [null],
    });

    this.setupSearch(
      this.statusSearch$,
      q => this.ticketService.searchTicketStatuses(q),
      this.statuses,
      this.statusesLoading,
      () => this.statusSeed
    );
    this.setupSearch(
      this.userSearch$,
      q => this.ticketService.searchUsers(q),
      this.users,
      this.usersLoading,
      () => this.userSeed
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Pipeline de busca das opções: mesmo comportamento dos demais campos com busca remota
   * (debounce de 400ms, troca de requisição e lista vazia em caso de erro).
   */
  private setupSearch(
    subject: Subject<string>,
    searchFn: (query: string) => Observable<any[]>,
    listSignal: { set: (v: any[]) => void },
    loadingSignal: { set: (v: boolean) => void },
    seedGetter: () => { id: string } | null
  ): void {
    subject
      .pipe(
        debounceTime(400),
        tap(() => {
          loadingSignal.set(true);
          this.cdr.markForCheck();
        }),
        switchMap(query => searchFn(query).pipe(catchError(() => of([] as any[])))),
        takeUntil(this.destroy$)
      )
      .subscribe(items => {
        listSignal.set(this.withSeed(items, seedGetter()));
        loadingSignal.set(false);
        this.cdr.markForCheck();
      });
  }

  /** Garante o registro selecionado na listagem — no topo, quando a busca não o trouxe. */
  private withSeed(items: any[], seed: { id: string } | null): any[] {
    if (!seed || items.some(item => item?.id === seed.id)) {
      return items;
    }
    return [seed, ...items];
  }

  onStatusFilter(event: any): void {
    this.statusSearch$.next(event?.filter ?? '');
  }

  onUserFilter(event: any): void {
    this.userSearch$.next(event?.filter ?? '');
  }

  async open(pub: QualityPublication | null, ticketId: string): Promise<void> {
    this.ticketId = ticketId;
    this.editingId = pub?.id ?? null;
    this.tags = pub?.tags ? pub.tags.split(',').map(t => t.trim()).filter(Boolean) : [];

    this.publicar.set(pub?.publicar ?? false);

    this.form.reset({
      titulo: pub?.titulo ?? '',
      descricao: pub?.descricao ?? '',
      link: pub?.link ?? '',
      data: pub?.data ? new Date(pub.data) : null,
      ticket_status_id: pub?.ticket_status_id ?? null,
      user_id: pub?.user_id ?? null,
    });

    // O id vem do campo achatado do resource; da relação só aproveitamos o rótulo, para não
    // depender de como o model aninhado serializa o UUID binário.
    this.statusSeed =
      pub?.ticket_status_id && pub.ticket_status?.description
        ? { id: pub.ticket_status_id, description: pub.ticket_status.description }
        : null;
    this.userSeed =
      pub?.user_id && pub.user?.name ? { id: pub.user_id, name: pub.user.name } : null;

    await Promise.all([this.loadStatuses(), this.loadUsers()]);
    this.visible.set(true);
    this.cdr.markForCheck();
  }

  async loadStatuses(): Promise<void> {
    this.statusesLoading.set(true);
    try {
      const result = await firstValueFrom(this.ticketService.searchTicketStatuses(''));
      this.statuses.set(this.withSeed(result ?? [], this.statusSeed));
    } catch {
    } finally {
      this.statusesLoading.set(false);
    }
  }

  async loadUsers(): Promise<void> {
    this.usersLoading.set(true);
    try {
      const result = await firstValueFrom(this.ticketService.searchUsers(''));
      this.users.set(this.withSeed(result ?? [], this.userSeed));
    } catch {
    } finally {
      this.usersLoading.set(false);
    }
  }

  async save(): Promise<void> {
    this.saving.set(true);
    try {
      const val = this.form.value;
      const payload: Partial<QualityPublication> = {
        titulo: val.titulo || null,
        tags: this.tags.length ? this.tags.join(',') : null,
        publicar: this.publicar(),
        descricao: val.descricao || null,
        link: val.link || null,
        data: val.data ? (val.data as Date).toISOString() : null,
        ticket_status_id: val.ticket_status_id || null,
        user_id: val.user_id || null,
        ticket_id: this.ticketId,
      };

      const editingId = this.editingId;
      if (editingId) {
        await this.qualityService.update(editingId, payload);
      } else {
        await this.qualityService.create(payload);
      }

      this.toastService.show({ color: 'success', message: 'Publicação salva com sucesso!' });
      this.visible.set(false);
      this.onSave.emit();
    } catch {
      this.toastService.show({ color: 'error', message: 'Erro ao salvar publicação' });
    } finally {
      this.saving.set(false);
    }
  }

  cancel(): void {
    this.visible.set(false);
  }
}
